import { type ReactElement } from 'react';
import { type SupportPartyCardView, type SupportParty, type SupportSubjectCardView } from '@vello/api-client';
import { Badge, Button, Notice, Stack, Text, Row, Ink, Box } from '@vello/web-theme';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { usePhoneText } from './phone-text';

/**
 * The support subject card (W5-SUPPORT-DESK, D384): exactly what the server's support projection returns — a name, a
 * masked phone, the account status and the role; a driver's plate, model and colour; a desk guest's first name and
 * partner account. It has no field for an email, a document, an address or KYC, so none can be shown. A full phone
 * appears only after a logged reveal (`revealed`), and only for the party it was revealed for.
 */
export function SubjectCard({
  card,
  revealable = [],
  revealed = {},
  onReveal,
}: {
  card: SupportSubjectCardView | undefined;
  /** The parties the server allows a reveal for on this ticket (empty before assign-to-self). */
  revealable?: readonly SupportParty[];
  revealed?: Readonly<Partial<Record<SupportParty, string>>>;
  onReveal?: (party: SupportParty, name: string) => void;
}): ReactElement {
  const { t } = useT();
  if (card === undefined || card.parties.length === 0) return <Notice tone="neutral">{t('desk.card.noCard')}</Notice>;
  return (
    <Stack gap={3}>
      {card.parties.map((p) => (
        <PartyRow key={p.role} party={p} {...(revealed[p.role] !== undefined ? { fullPhone: revealed[p.role] } : {})} {...(onReveal !== undefined && revealable.includes(p.role) && revealed[p.role] === undefined ? { onReveal } : {})} />
      ))}
      <Text as="p" role="caption" tone="tertiary">{t('desk.card.privacy')}</Text>
    </Stack>
  );
}

function PartyRow({ party, fullPhone, onReveal }: { party: SupportPartyCardView; fullPhone?: string; onReveal?: (party: SupportParty, name: string) => void }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const phoneOf = usePhoneText();
  const name = party.displayName ?? t('desk.card.unnamed');
  const rows: [string, string][] = [];
  if (party.phoneMasked !== undefined) rows.push([t('desk.card.phone'), phoneOf(party.phoneMasked)]);
  if (fullPhone !== undefined) rows.push([t('desk.reveal.fullPhone'), phoneOf(fullPhone)]);
  if (party.vehicle !== undefined) rows.push([t('desk.card.vehicle'), [party.vehicle.plate, party.vehicle.model, party.vehicle.colour].filter((x): x is string => x !== undefined).join(' · ')]);
  if (party.partnerAccount !== undefined) rows.push([t('desk.card.partner'), party.partnerAccount.name]);
  return (
    <Box as="section" gap={2} pad={4} aria-label={`${v('audit.contact_party', party.role)}: ${name}`}>
      <Row gap={2} align="center" wrap>
        <Badge tone="neutral">{v('audit.contact_party', party.role)}</Badge>
        <Text as="strong" role="label">{name}</Text>
        {party.accountStatus !== undefined && <Badge tone={party.accountStatus === 'ACTIVE' ? 'success' : 'warning'}>{v('identity.user_status', party.accountStatus)}</Badge>}
      </Row>
      {rows.map(([k, v]) => (
        <Text as="div" role="caption" key={k}>
          <Row gap={3} align="stretch" justify="between">
            <Ink tone="secondary">{k}</Ink>
            <Ink strong mono>{v}</Ink>
          </Row>
        </Text>
      ))}
      {onReveal !== undefined && party.hasContact && (
        <div>
          <Button variant="secondary" icon="lock" onClick={() => onReveal(party.role, name)}>{t('desk.reveal.action')}</Button>
        </div>
      )}
    </Box>
  );
}
